import React from 'react';
import { Card, Collapse, IconButton, Grid, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import DiffRenderer from './DiffRenderer';

function CPQAttributesReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = React.useState(null);
  const [expandedNestedItems, setExpandedNestedItems] = React.useState({});
  const [productDataMap, setProductDataMap] = React.useState({});
  const [selectedProductId, setSelectedProductId] = React.useState(null);

  const attributeColumns = [
    { name: 'attribute', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  React.useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    console.log('productData productAttributes', productData[0].productAttributes);

    const generatedProductDataMap = {};

    productData.forEach((productDiff) => {
      const generatedConfigAttributeRows = { new: [], changed: [], deleted: [], same: [] };
      const generatedClassificationAttributeRows = { new: [], changed: [], deleted: [], same: [] };
      const generatedProductAttributeRows = { new: [], changed: [], deleted: [], same: [] };

      // Process Configuration Attributes (Top Level)
      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.configurationAttributes?.[type] || []).forEach((attribute) => {
          generatedConfigAttributeRows[type].push({
            id: attribute.sfId || attribute.id,
            attribute: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {attribute.name || 'Configuration Attribute'}
              </ArgonTypography>
            ),
            diffs: attribute.changes || [],
            sObjectName: 'SBQQ__ConfigurationAttribute__c',
            type: type,
          });
        });
      });

      // Process Classification Attributes
      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.classificationAttributes?.[type] || []).forEach((attribute) => {
          generatedClassificationAttributeRows[type].push({
            id: attribute.recordId || attribute.id,
            attribute: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {attribute.name || 'Classification Attribute'}
              </ArgonTypography>
            ),
            diffs: attribute.changes || [],
            sObjectName: 'ClassificationAttribute__c',
            type: type,
          });
        });
      });

      // Process Product Attributes (New Nested Structure)
      if (productDiff.productAttributes) {
        // 1. Configuration Product Attributes within productAttributes
        ['new', 'changed', 'deleted', 'same'].forEach((type) => {
          (productDiff.productAttributes.configurationProductAttributes?.[type] || []).forEach((attribute, index) => {
            generatedProductAttributeRows[type].push({
              id: `pa-config-${attribute.sfid || attribute.id || index}`,
              attribute: (
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  {attribute.name || 'Configuration Product Attribute'}
                </ArgonTypography>
              ),
              diffs: attribute.changes || [],
              sObjectName: 'SBQQ__ConfigurationAttribute__c',
              type: type,
            });
          });
        });

        // 2. Product Option Attributes within productAttributes
        ['new', 'changed', 'deleted', 'same'].forEach((type) => {
          (productDiff.productAttributes.productOptionAttributes?.[type] || []).forEach((option, index) => {
            generatedProductAttributeRows[type].push({
              id: `pa-option-${option.sfid || option.id || index}`,
              attribute: (
                <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                  {`Option Attribute: ${option.name || option.sfid || option.id || index + 1}`}
                </ArgonTypography>
              ),
              diffs: option.changes || [],
              sObjectName: 'SBQQ__ProductOption__c',
              type: type,
              nestedData: option, // Pass the entire option object for deep rendering
            });
          });
        });
      }

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id] = {
        configAttributeRows: generatedConfigAttributeRows,
        classificationAttributeRows: generatedClassificationAttributeRows,
        productAttributeRows: generatedProductAttributeRows,
        productName: productDiff.product.name,
      };
    });
    setProductDataMap(generatedProductDataMap);

    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  const handleNestedItemClick = (itemId) => {
    setExpandedNestedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getSObjectNameForSection = (sectionName) => {
    const sObjectMap = {
      'Product Attribute Set': 'SBQQ__ProductOptionAttributeSet__c',
      'Attribute Set': 'SBQQ__AttributeSet__c',
      'Attribute Item': 'SBQQ__AttributeItem__c',
      'Configuration Item Attribute': 'SBQQ__ConfigurationAttribute__c',
      productAttributeSets: 'SBQQ__ProductOptionAttributeSet__c',
      attributeSets: 'SBQQ__AttributeSet__c',
      attributeItems: 'SBQQ__AttributeItem__c',
      configurationAttributes: 'SBQQ__ConfigurationAttribute__c',
    };
    return sObjectMap[sectionName] || 'Unknown';
  };

  // --- RECURSIVE RENDERER ---
  const renderNestedDiffSection = (diffData, sectionName, parentPath, depth = 0) => {
    if (!diffData || typeof diffData !== 'object') return null;

    const indentLevel = depth * 2; // 16px per depth level

    return (
      <ArgonBox>
        {['new', 'changed', 'deleted', 'same'].map((type) => {
          const items = diffData[type] || [];
          if (items.length === 0 && type !== 'same') return null;
          if (items.length === 0 && !showSameFields) return null;

          return items.map((item, index) => {
            const uniqueId = `${parentPath}-${type}-${index}-${item.sfid || item.id || index}`;

            // Check for nested content based on the new structure
            const hasNestedContent = !!(
              item.productAttributeSets ||
              item.attributeSets ||
              item.attributeItems ||
              item.configurationItemAttributes
            );

            // Debug logging for nested content detection
            if (sectionName === 'Product Attribute Set' && hasNestedContent) {
              console.log(`[DEBUG] Found nested content for ${sectionName}:`, {
                productAttributeSets: !!item.productAttributeSets,
                attributeSets: !!item.attributeSets,
                attributeItems: !!item.attributeItems,
                configurationItemAttributes: !!item.configurationItemAttributes,
                item: item,
              });
            }

            return (
              <ArgonBox
                key={uniqueId}
                mb={1}
                p={1.5}
                bgcolor={grey[100]}
                borderRadius={1}
                sx={{ ml: `${indentLevel}px` }}
              >
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => hasNestedContent && handleNestedItemClick(uniqueId)}
                  sx={{ cursor: hasNestedContent ? 'pointer' : 'default' }}
                >
                  <ArgonTypography variant="caption" fontWeight="bold">
                    {`${sectionName}: ${item.IDFieldList?.Name || item.name || item.sfid || `Item ${index + 1}`}`}
                  </ArgonTypography>
                  {hasNestedContent && (
                    <IconButton size="small">
                      <Icon>{expandedNestedItems[uniqueId] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                    </IconButton>
                  )}
                </ArgonBox>

                {/* Render field changes for this item */}
                {item.changes && item.changes.length > 0 && (
                  <ArgonBox mt={1}>
                    {console.log(`[DEBUG] Nested DiffRenderer for ${sectionName}:`, {
                      changes: item.changes,
                      sObjectName: getSObjectNameForSection(sectionName),
                      showSameFields: showSameFields,
                      changesLength: item.changes?.length || 0,
                      changeTypes: item.changes?.map((c) => c.type) || [],
                      hasSameChanges: item.changes?.some((c) => c.type === 'same') || false,
                      hasNewChanges: item.changes?.some((c) => c.type === 'new') || false,
                      hasChangedChanges: item.changes?.some((c) => c.type === 'changed') || false,
                    })}
                    <DiffRenderer
                      changes={item.changes}
                      sObjectName={getSObjectNameForSection(sectionName)}
                      orgType={selectedOrg.type.toLowerCase()}
                      showSameFields={true}
                    />
                  </ArgonBox>
                )}

                {/* Render nested content */}
                {hasNestedContent && (
                  <Collapse in={expandedNestedItems[uniqueId]} timeout="auto" unmountOnExit>
                    <ArgonBox mt={1} pl={2} borderLeft={`2px solid ${grey[300]}`}>
                      {/* Product Attribute Sets */}
                      {item.productAttributeSets &&
                        renderNestedDiffSection(
                          item.productAttributeSets,
                          'Product Attribute Set',
                          `${uniqueId}-pas`,
                          depth + 1
                        )}

                      {/* Attribute Sets */}
                      {item.attributeSets &&
                        renderNestedDiffSection(item.attributeSets, 'Attribute Set', `${uniqueId}-as`, depth + 1)}

                      {/* Attribute Items */}
                      {item.attributeItems &&
                        renderNestedDiffSection(item.attributeItems, 'Attribute Item', `${uniqueId}-ai`, depth + 1)}

                      {/* Configuration Item Attributes */}
                      {item.configurationItemAttributes &&
                        renderNestedDiffSection(
                          item.configurationItemAttributes,
                          'Configuration Item Attribute',
                          `${uniqueId}-cia`,
                          depth + 1
                        )}
                    </ArgonBox>
                  </Collapse>
                )}
              </ArgonBox>
            );
          });
        })}
      </ArgonBox>
    );
  };

  const renderExpandedContent = (row) => {
    // Debug logging for nested data
    if (row.nestedData) {
      console.log(`[DEBUG] renderExpandedContent for row ${row.id}:`, {
        hasNestedData: !!row.nestedData,
        productAttributeSets: !!row.nestedData.productAttributeSets,
        attributeSets: !!row.nestedData.attributeSets,
        attributeItems: !!row.nestedData.attributeItems,
        configurationItemAttributes: !!row.nestedData.configurationItemAttributes,
        nestedData: row.nestedData,
      });
    }

    return (
      <TableRow>
        <TableCell colSpan={attributeColumns.length} sx={{ p: 0 }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <ArgonBox p={3} bgcolor={grey[50]}>
              {/* Render field changes for this attribute */}
              {console.log(`[DEBUG] DiffRenderer for row ${row.id}:`, {
                changes: row.diffs,
                sObjectName: row.sObjectName,
                showSameFields: showSameFields,
                changesLength: row.diffs?.length || 0,
                changeTypes: row.diffs?.map((c) => c.type) || [],
                hasSameChanges: row.diffs?.some((c) => c.type === 'same') || false,
                hasNewChanges: row.diffs?.some((c) => c.type === 'new') || false,
                hasChangedChanges: row.diffs?.some((c) => c.type === 'changed') || false,
              })}
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />

              {/* Render nested content if it exists */}
              {row.nestedData && (
                <ArgonBox mt={2}>
                  {/* Product Attribute Sets */}
                  {row.nestedData.productAttributeSets &&
                    renderNestedDiffSection(
                      row.nestedData.productAttributeSets,
                      'Product Attribute Set',
                      `${row.id}-sets`
                    )}

                  {/* Attribute Sets */}
                  {row.nestedData.attributeSets &&
                    renderNestedDiffSection(row.nestedData.attributeSets, 'Attribute Set', `${row.id}-as`)}

                  {/* Attribute Items */}
                  {row.nestedData.attributeItems &&
                    renderNestedDiffSection(row.nestedData.attributeItems, 'Attribute Item', `${row.id}-ai`)}

                  {/* Configuration Item Attributes */}
                  {row.nestedData.configurationItemAttributes &&
                    renderNestedDiffSection(
                      row.nestedData.configurationItemAttributes,
                      'Configuration Item Attribute',
                      `${row.id}-cia`
                    )}
                </ArgonBox>
              )}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  // ... (The rest of the component remains the same)
  const renderProductSection = (productName, productData) => {
    const { configAttributeRows, classificationAttributeRows, productAttributeRows } = productData;

    const renderTableSection = (title, rows, columns) => {
      if (!rows || rows.length === 0) {
        return null;
      }
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <StageExpandableTable
                  rows={rows}
                  columns={columns}
                  renderRowSubComponent={({ row }) => renderExpandedContent(row)}
                />
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) => {
      return rows.map((row) => ({
        ...row,
        renderExpandedContent: () => renderExpandedContent(row),
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
    };

    const hasConfigAttributes = ['new', 'changed', 'deleted', 'same'].some(
      (type) => configAttributeRows[type].length > 0
    );
    const hasClassificationAttributes = ['new', 'changed', 'deleted', 'same'].some(
      (type) => classificationAttributeRows[type].length > 0
    );
    const hasProductAttributes = ['new', 'changed', 'deleted', 'same'].some(
      (type) => productAttributeRows[type].length > 0
    );

    if (!hasConfigAttributes && !hasClassificationAttributes && !hasProductAttributes) {
      return (
        <ArgonBox key={productName} mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                There is no attributes data available for this product.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            configAttributeRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Configuration Attributes`,
              formattedRows(configAttributeRows[type]),
              attributeColumns
            )
        )}

        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            classificationAttributeRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Classification Attributes`,
              formattedRows(classificationAttributeRows[type]),
              attributeColumns
            )
        )}

        {['new', 'changed', 'same', 'deleted'].map(
          (type) =>
            productAttributeRows[type].length > 0 &&
            renderTableSection(
              `${type.charAt(0).toUpperCase() + type.slice(1)} Product Attributes`,
              formattedRows(productAttributeRows[type]),
              attributeColumns
            )
        )}
      </ArgonBox>
    );
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  Select a product to view its attributes.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

CPQAttributesReview.propTypes = {
  selectedOrg: PropTypes.object.isRequired,
  productData: PropTypes.array.isRequired,
  showSameFields: PropTypes.bool.isRequired,
};

export default CPQAttributesReview;
